// src/helpers/ticketQrDecode.ts
// Giải mã nội dung QR quét được (URL đầy đủ in trên vé HOẶC token trần) thành thông tin vé
// {kỳ phát hành, số vé, ký hiệu}. Dùng chung cho mọi màn cần quét QR vé (Giao vé, Thu hồi vé...).
//
// Ưu tiên xác thực chữ ký OFFLINE bằng public key (nhanh, không round-trip server) qua
// verifyAndDecodeTicketToken; chỉ gọi API backend cho định dạng token cũ (AES-256-GCM) mà xác thực
// offline trả về null.

import { extractTicketTokenFromScan, verifyAndDecodeTicketToken } from './ticketTokenVerifier';

export interface ScannedTicket {
  lotteryPeriodId: number;
  ticketNumber: string;
  symbol: string;
  planCode: string;
}

// Các "ứng viên" token tách từ nội dung QR — thử nhiều cách vì máy quét trả về đủ kiểu:
//   URL đầy đủ ".../v/{token}", URL không có "/" trước token ".../v{token}", có query "?token=...",
//   route hash "#/v/{token}", hoặc chuỗi bị máy quét (sai layout bàn phím) làm hỏng dấu ":" "/".
// Cách bao quát nhất: lấy ĐOẠN base64url DÀI NHẤT bất kỳ trong chuỗi — luôn chính là token.
// Token luôn được XÁC THỰC CHỮ KÝ nên ứng viên sai chỉ đơn giản bị loại, không có rủi ro bảo mật.
const TOKEN_LIKE = /[A-Za-z0-9_-]{20,}/g;

export const ticketTokenCandidates = (rawScan: string): string[] => {
  const scan = (rawScan || '').trim();
  const ordered: string[] = [];
  const push = (value?: string | null) => {
    const v = (value || '').trim();
    if (v.length >= 20 && !ordered.includes(v)) ordered.push(v);
    // ".../v{token}" ghép thẳng không có "/" ngăn cách -> thử bỏ tiền tố "v".
    if (/^v[A-Za-z0-9_-]{20,}$/.test(v)) push(v.slice(1));
  };

  // 1) Đoạn cuối path (kiểu URL in trên vé).
  push(extractTicketTokenFromScan(scan));

  // 2) Nếu là URL hợp lệ: thử các giá trị query param + đoạn cuối route hash.
  try {
    const url = new URL(scan);
    url.searchParams.forEach((value) => push(value));
    const hashSegs = url.hash.replace(/^#\/?/, '').split(/[/?#]/).filter(Boolean);
    if (hashSegs.length > 0) push(hashSegs[hashSegs.length - 1]);
  } catch {
    /* không phải URL — bỏ qua */
  }

  // 3) Bao quát: đoạn base64url dài nhất bất kỳ trong chuỗi (chịu được URL bị làm hỏng dấu phân cách).
  const runs = scan.match(TOKEN_LIKE);
  if (runs && runs.length > 0) {
    [...runs].sort((a, b) => b.length - a.length).forEach(push);
  }

  // 4) Chính chuỗi quét (trường hợp token trần).
  push(scan);

  return ordered.slice(0, 8);
};

export const decodeScannedTicket = async (
  rawScan: string,
  decodeTokenApi: (token: string) => Promise<any>,
): Promise<ScannedTicket | null> => {
  const candidates = ticketTokenCandidates(rawScan);
  if (candidates.length === 0) return null;

  // 1) Xác thực chữ ký offline (JWT RS256 / ECDSA nhị phân gọn).
  for (const token of candidates) {
    const offline = await verifyAndDecodeTicketToken(token);
    if (offline) {
      return {
        lotteryPeriodId: offline.lotteryPeriodId,
        ticketNumber: offline.ticketNumber,
        symbol: offline.symbol,
        planCode: offline.planCode,
      };
    }
  }

  // 2) Fallback: token định dạng cũ AES-256-GCM -> nhờ backend giải mã. Chỉ gửi ứng viên trông
  //    giống token thật (1 đoạn base64url) — bỏ qua chuỗi URL/đoạn bị hỏng để không gọi API vô ích.
  for (const token of candidates.filter((t) => /^[A-Za-z0-9_-]{20,}$/.test(t))) {
    try {
      const response: any = await decodeTokenApi(token);
      const payload = response?.data || response;
      if (payload?.ticketNumber && payload?.symbol) {
        return {
          lotteryPeriodId: Number(payload.lotteryPeriodId),
          ticketNumber: String(payload.ticketNumber),
          symbol: String(payload.symbol),
          planCode: payload.planCode ? String(payload.planCode) : '',
        };
      }
    } catch {
      /* thử ứng viên kế tiếp */
    }
  }

  return null;
};
